import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe, Trash2, ChevronRight, Loader2, AlertCircle,
  RefreshCw, TrendingUp, TrendingDown,
} from 'lucide-react';
import { PageContainer, PageHeader } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { diagnoseApi, DiagnoseHistoryItem, DiagnoseVersionHistoryItem } from '@/api/diagnoseApi';
import { cn } from '@/lib/utils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function scoreColor(score: number | null): string {
  if (score === null) return 'text-gray-400';
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-orange-500';
  if (score >= 50) return 'text-yellow-600';
  return 'text-red-600';
}

function statusBadge(status: string) {
  switch (status) {
    case 'DONE':
      return <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">완료</Badge>;
    case 'RUNNING':
      return <Badge className="bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100">진행중</Badge>;
    case 'PENDING':
      return <Badge className="bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-100">대기중</Badge>;
    default:
      return <Badge className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100">실패</Badge>;
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', {
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function ScoreChange({ change }: { change: number | null }) {
  if (change === null || change === 0) return null;
  return (
    <span className={cn(
      'inline-flex items-center gap-0.5 text-xs font-medium',
      change > 0 ? 'text-green-600' : 'text-red-600'
    )}>
      {change > 0
        ? <TrendingUp className="w-3 h-3" />
        : <TrendingDown className="w-3 h-3" />}
      {change > 0 ? '+' : ''}{change}
    </span>
  );
}

// ─── Version History Modal ────────────────────────────────────────────────────

function VersionHistoryModal({
  open,
  targetUrl,
  onClose,
  onSelectVersion,
}: {
  open: boolean;
  targetUrl: string;
  onClose: () => void;
  onSelectVersion: (jobId: string) => void;
}) {
  const [versions, setVersions] = useState<DiagnoseVersionHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !targetUrl) return;
    setLoading(true);
    setError(null);

    // MD5 hash computation — simple pure-JS implementation for urlHash
    md5Hex(targetUrl).then(hash => {
      diagnoseApi.getVersionHistory(hash)
        .then(setVersions)
        .catch(() => setError('버전 이력을 불러오지 못했습니다.'))
        .finally(() => setLoading(false));
    });
  }, [open, targetUrl]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>버전 이력</DialogTitle>
          <p className="text-xs text-gray-500 truncate mt-1">{targetUrl}</p>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && !error && (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {versions.map(v => (
              <div
                key={v.jobId}
                className="border rounded-lg p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                onClick={() => { onSelectVersion(v.jobId); onClose(); }}
              >
                <div className="shrink-0">
                  <Badge className="bg-gray-100 text-gray-700 border-gray-200">v{v.version}</Badge>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {statusBadge(v.status)}
                    {v.completedAt && (
                      <span className="text-xs text-gray-400">{formatDate(v.completedAt)}</span>
                    )}
                  </div>
                  {v.scores?.accessibility != null && (
                    <div className="flex gap-3 mt-1 text-xs text-gray-600">
                      <span>접근성 <span className={cn('font-semibold', scoreColor(v.scores?.accessibility ?? null))}>
                        {v.scores?.accessibility ?? '—'}
                      </span></span>
                      <span>SEO <span className={cn('font-semibold', scoreColor(v.scores.seo))}>
                        {v.scores.seo ?? '—'}
                      </span></span>
                      {v.scoreChange && (
                        <ScoreChange change={v.scoreChange.accessibility} />
                      )}
                    </div>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Simple MD5 (pure JS, no external dep) ───────────────────────────────────
// Used to compute urlHash for the versions endpoint
// Note: This matches Java's DigestUtils.md5Hex(targetUrl)

async function md5Hex(text: string): Promise<string> {
  // Fallback: we use SubtleCrypto SHA-1 isn't ideal; instead implement RFC 1321 MD5 inline.
  // Since WebCrypto doesn't support MD5, use a minimal pure-JS implementation.
  return pureMd5(text);
}

function pureMd5(str: string): string {
  function safeAdd(x: number, y: number): number {
    const lsw = (x & 0xffff) + (y & 0xffff);
    return (((x >> 16) + (y >> 16) + (lsw >> 16)) << 16) | (lsw & 0xffff);
  }
  function bitRotateLeft(num: number, cnt: number): number {
    return (num << cnt) | (num >>> (32 - cnt));
  }
  function md5cmn(q: number, a: number, b: number, x: number, s: number, t: number): number {
    return safeAdd(bitRotateLeft(safeAdd(safeAdd(a, q), safeAdd(x, t)), s), b);
  }
  function md5ff(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
    return md5cmn((b & c) | (~b & d), a, b, x, s, t);
  }
  function md5gg(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
    return md5cmn((b & d) | (c & ~d), a, b, x, s, t);
  }
  function md5hh(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
    return md5cmn(b ^ c ^ d, a, b, x, s, t);
  }
  function md5ii(a: number, b: number, c: number, d: number, x: number, s: number, t: number) {
    return md5cmn(c ^ (b | ~d), a, b, x, s, t);
  }

  const m = unescape(encodeURIComponent(str));
  const n = m.length;
  const state: number[] = [1732584193, -271733879, -1732584194, 271733878];

  let i: number;
  const blks: number[] = new Array(Math.ceil((n + 9) / 64) * 16).fill(0);
  for (i = 0; i < n; i++) blks[i >> 2] |= m.charCodeAt(i) << ((i % 4) * 8);
  blks[i >> 2] |= 0x80 << ((i % 4) * 8);
  blks[blks.length - 2] = n * 8;

  for (let blkStart = 0; blkStart < blks.length; blkStart += 16) {
    const [a0, b0, c0, d0] = state;
    let [a, b, c, d] = state;
    const x = blks.slice(blkStart, blkStart + 16);

    a = md5ff(a,b,c,d,x[0],7,-680876936);   d = md5ff(d,a,b,c,x[1],12,-389564586);
    c = md5ff(c,d,a,b,x[2],17,606105819);   b = md5ff(b,c,d,a,x[3],22,-1044525330);
    a = md5ff(a,b,c,d,x[4],7,-176418897);   d = md5ff(d,a,b,c,x[5],12,1200080426);
    c = md5ff(c,d,a,b,x[6],17,-1473231341); b = md5ff(b,c,d,a,x[7],22,-45705983);
    a = md5ff(a,b,c,d,x[8],7,1770035416);   d = md5ff(d,a,b,c,x[9],12,-1958414417);
    c = md5ff(c,d,a,b,x[10],17,-42063);     b = md5ff(b,c,d,a,x[11],22,-1990404162);
    a = md5ff(a,b,c,d,x[12],7,1804603682);  d = md5ff(d,a,b,c,x[13],12,-40341101);
    c = md5ff(c,d,a,b,x[14],17,-1502002290);b = md5ff(b,c,d,a,x[15],22,1236535329);

    a = md5gg(a,b,c,d,x[1],5,-165796510);   d = md5gg(d,a,b,c,x[6],9,-1069501632);
    c = md5gg(c,d,a,b,x[11],14,643717713);  b = md5gg(b,c,d,a,x[0],20,-373897302);
    a = md5gg(a,b,c,d,x[5],5,-701558691);   d = md5gg(d,a,b,c,x[10],9,38016083);
    c = md5gg(c,d,a,b,x[15],14,-660478335); b = md5gg(b,c,d,a,x[4],20,-405537848);
    a = md5gg(a,b,c,d,x[9],5,568446438);    d = md5gg(d,a,b,c,x[14],9,-1019803690);
    c = md5gg(c,d,a,b,x[3],14,-187363961);  b = md5gg(b,c,d,a,x[8],20,1163531501);
    a = md5gg(a,b,c,d,x[13],5,-1444681467); d = md5gg(d,a,b,c,x[2],9,-51403784);
    c = md5gg(c,d,a,b,x[7],14,1735328473);  b = md5gg(b,c,d,a,x[12],20,-1926607734);

    a = md5hh(a,b,c,d,x[5],4,-378558);      d = md5hh(d,a,b,c,x[8],11,-2022574463);
    c = md5hh(c,d,a,b,x[11],16,1839030562); b = md5hh(b,c,d,a,x[14],23,-35309556);
    a = md5hh(a,b,c,d,x[1],4,-1530992060);  d = md5hh(d,a,b,c,x[4],11,1272893353);
    c = md5hh(c,d,a,b,x[7],16,-155497632);  b = md5hh(b,c,d,a,x[10],23,-1094730640);
    a = md5hh(a,b,c,d,x[13],4,681279174);   d = md5hh(d,a,b,c,x[0],11,-358537222);
    c = md5hh(c,d,a,b,x[3],16,-722521979);  b = md5hh(b,c,d,a,x[6],23,76029189);
    a = md5hh(a,b,c,d,x[9],4,-640364487);   d = md5hh(d,a,b,c,x[12],11,-421815835);
    c = md5hh(c,d,a,b,x[15],16,530742520);  b = md5hh(b,c,d,a,x[2],23,-995338651);

    a = md5ii(a,b,c,d,x[0],6,-198630844);   d = md5ii(d,a,b,c,x[7],10,1126891415);
    c = md5ii(c,d,a,b,x[14],15,-1416354905);b = md5ii(b,c,d,a,x[5],21,-57434055);
    a = md5ii(a,b,c,d,x[12],6,1700485571);  d = md5ii(d,a,b,c,x[3],10,-1894986606);
    c = md5ii(c,d,a,b,x[10],15,-1051523);   b = md5ii(b,c,d,a,x[1],21,-2054922799);
    a = md5ii(a,b,c,d,x[8],6,1873313359);   d = md5ii(d,a,b,c,x[15],10,-30611744);
    c = md5ii(c,d,a,b,x[6],15,-1560198380); b = md5ii(b,c,d,a,x[13],21,1309151649);
    a = md5ii(a,b,c,d,x[4],6,-145523070);   d = md5ii(d,a,b,c,x[11],10,-1120210379);
    c = md5ii(c,d,a,b,x[2],15,718787259);   b = md5ii(b,c,d,a,x[9],21,-343485551);

    state[0] = safeAdd(a, a0);
    state[1] = safeAdd(b, b0);
    state[2] = safeAdd(c, c0);
    state[3] = safeAdd(d, d0);
  }

  return state.map(n =>
    ('00000000' + ((n < 0 ? n + 4294967296 : n).toString(16))).slice(-8)
      .match(/../g)!.reverse().join('')
  ).join('');
}

// ─── History Item Row ─────────────────────────────────────────────────────────

function HistoryRow({
  item,
  onView,
  onVersions,
  onDelete,
}: {
  item: DiagnoseHistoryItem;
  onView: () => void;
  onVersions: () => void;
  onDelete: () => void;
}) {
  const a11yChange = item.scoreChange?.accessibility ?? null;
  const seoChange = item.scoreChange?.seo ?? null;

  return (
    <div className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        {/* URL & meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {statusBadge(item.status)}
            <Badge className="text-xs bg-gray-100 text-gray-700 border border-gray-200">v{item.version}</Badge>
            {item.isLatest && (
              <Badge className="bg-primary/10 text-primary border-primary/20 text-xs hover:bg-primary/10">최신</Badge>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span className="text-sm font-medium text-gray-700 truncate">{item.targetUrl}</span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{formatDate(item.createdAt)}</p>
        </div>

        {/* Scores */}
        {item.status === 'DONE' && (
          <div className="flex gap-4 shrink-0">
            <div className="text-center">
              <p className={cn('text-lg font-bold', scoreColor(item.scores?.accessibility ?? null))}>
                {item.scores?.accessibility ?? '—'}
              </p>
              <p className="text-xs text-gray-400">접근성</p>
              <ScoreChange change={a11yChange} />
            </div>
            <div className="text-center">
              <p className={cn('text-lg font-bold', scoreColor(item.scores?.seo ?? null))}>
                {item.scores?.seo ?? '—'}
              </p>
              <p className="text-xs text-gray-400">SEO</p>
              <ScoreChange change={seoChange} />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {item.status === 'DONE' && (
            <Button variant="outline" size="sm" onClick={onView} className="gap-1">
              <ChevronRight className="w-3.5 h-3.5" />
              결과 보기
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onVersions} className="gap-1 text-gray-500">
            <RefreshCw className="w-3.5 h-3.5" />
            버전
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={onDelete}
            className="text-red-400 hover:text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Confirmation Dialog ───────────────────────────────────────────────

function DeleteConfirmDialog({
  open,
  onClose,
  onConfirm,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>진단 결과 삭제</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">이 진단 결과를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.</p>
        <div className="flex gap-3 justify-end mt-4">
          <Button variant="outline" onClick={onClose} disabled={loading}>취소</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            삭제
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DiagnoseHistoryPage() {
  const navigate = useNavigate();

  const [items, setItems] = useState<DiagnoseHistoryItem[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [versionsTarget, setVersionsTarget] = useState<string | null>(null); // targetUrl

  const loadPage = useCallback(async (p: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await diagnoseApi.getHistory(p, 20);
      setItems(data.content);
      setPage(data.number);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch {
      setError('진단 이력을 불러오지 못했습니다.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadPage(0); }, [loadPage]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await diagnoseApi.deleteJob(deleteTarget);
      setItems(prev => prev.filter(i => i.jobId !== deleteTarget));
      setTotalElements(prev => prev - 1);
    } catch {
      setError('삭제에 실패했습니다.');
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title="진단 이력"
        description={`웹 접근성 & SEO 진단 결과 이력 ${totalElements > 0 ? `(총 ${totalElements}건)` : ''}`}
        action={
          <Button onClick={() => navigate('/diagnose')} className="gap-2">
            <Globe className="w-4 h-4" />
            새 진단 시작
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {/* Empty */}
      {!loading && items.length === 0 && !error && (
        <Card>
          <CardContent className="py-16 text-center">
            <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">진단 이력이 없습니다.</p>
            <Button onClick={() => navigate('/diagnose')} variant="outline">
              첫 번째 진단 시작
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Items */}
      {!loading && items.length > 0 && (
        <div className="space-y-3">
          {items.map(item => (
            <HistoryRow
              key={item.jobId}
              item={item}
              onView={() => navigate(`/diagnose?jobId=${item.jobId}`)}
              onVersions={() => setVersionsTarget(item.targetUrl)}
              onDelete={() => setDeleteTarget(item.jobId)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3">
          <Button
            variant="outline" size="sm"
            disabled={page === 0 || loading}
            onClick={() => loadPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm text-gray-600">{page + 1} / {totalPages}</span>
          <Button
            variant="outline" size="sm"
            disabled={page + 1 >= totalPages || loading}
            onClick={() => loadPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}

      {/* Version History Modal */}
      <VersionHistoryModal
        open={!!versionsTarget}
        targetUrl={versionsTarget ?? ''}
        onClose={() => setVersionsTarget(null)}
        onSelectVersion={(jobId) => navigate(`/diagnose?jobId=${jobId}`)}
      />

      {/* Delete Confirm */}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </PageContainer>
  );
}
